import { Box, Link } from "@material-ui/core";
import { getHistory, useRouter } from "@shoothill/core";
import { StoresInstance } from "Globals/Stores";
import { CustomInput } from "Globals/Styles/AppStyling";
import { StyledButton } from "Globals/Styles/ButtonStyles";
import { useObserver } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { LoginViewModel } from "./LoginViewModel";
import { BackgroundImageContainer, LoginWrapper, Row, WelcomeText, LoginForm, InputWrapper, ForgotPasswordLink, ErrorMessage } from "./LoginView.styles";

const domainStores = StoresInstance.Domain;

export const LoginView: React.FunctionComponent = () => {
    const [viewModel] = useState(() => new LoginViewModel());
    const { history, location } = useRouter();
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (StoresInstance.Domain.AccountStore.IsLoggedIn) {
            history.push("/");
        }
    }, []);

    const doSubmit = async (e: any) => {
        e.preventDefault();
        if (await viewModel.isModelValid()) {
            const apiResult = await viewModel.login();
            if (apiResult !== null) {
                if (apiResult.wasSuccessful) {
                    await domainStores.AccountStore.getLoginState(apiResult.payload as any);
                    const { from } = (location.state as any) || { from: { pathname: "/" } };
                    if (from) {
                        history.push(from.pathname);
                    }
                } else {
                    if (apiResult && apiResult.errors && apiResult.errors.length > 0) {
                        setErrorMessage("* " + apiResult.errors[0].message);
                    } else {
                        setErrorMessage("* Unknown error has occurred.");
                    }
                }
            }
        }
    };

    const isMobile = window.innerWidth <= 768;

    return useObserver(() => (
        <Box style={{ display: "flex", minHeight: "100vh" }}>
            <LoginWrapper>
                <LoginHeader isMobile={isMobile} />
                <LoginFormSection viewModel={viewModel} errorMessage={errorMessage} onSubmit={doSubmit} />
            </LoginWrapper>
            <LoginImageBackground />
        </Box>
    ));
};
// endregion

// region Local Components
interface ILoginHeaderProps {
    isMobile: boolean;
}

const LoginHeader: React.FC<ILoginHeaderProps> = ({ isMobile }) => (
    <>
        <Row>
            <img
                src="/hightownLogo.svg"
                alt="Hightown"
                loading="eager"
                decoding="async"
                style={isMobile ? { width: "250px" } : { cursor: "pointer", width: "300px" }}
                onClick={() => getHistory().push("/")}
            />
        </Row>
        <Row></Row>
        <Row>
            <WelcomeText>Welcome to the Hightown Housing Group customer application portal.</WelcomeText>
        </Row>
    </>
);

interface ILoginFormSectionProps {
    viewModel: LoginViewModel;
    errorMessage: string;
    onSubmit: (e: any) => Promise<void>;
}

const LoginFormSection: React.FC<ILoginFormSectionProps> = ({ viewModel, errorMessage, onSubmit }) => (
    <LoginForm onSubmit={onSubmit}>
        <LoginErrorMessage errorMessage={errorMessage} />
        <LoginEmailInput viewModel={viewModel} />
        <LoginPasswordInput viewModel={viewModel} />
        <LoginSubmitButton isLoading={viewModel.IsLoading} />
    </LoginForm>
);

interface ILoginErrorMessageProps {
    errorMessage: string;
}

const LoginErrorMessage: React.FC<ILoginErrorMessageProps> = ({ errorMessage }) => (
    <Row>{errorMessage !== "" && <ErrorMessage variant="caption">{errorMessage}</ErrorMessage>}</Row>
);

interface ILoginEmailInputProps {
    viewModel: LoginViewModel;
}

const LoginEmailInput: React.FC<ILoginEmailInputProps> = ({ viewModel }) => (
    <Row>
        <InputWrapper>
            <CustomInput type="email" label="Email Address" validateOnBlur={true} viewModel={viewModel} fieldName="emailAddress" />
        </InputWrapper>
    </Row>
);

interface ILoginPasswordInputProps {
    viewModel: LoginViewModel;
}

const LoginPasswordInput: React.FC<ILoginPasswordInputProps> = ({ viewModel }) => (
    <Row>
        <Box>
            <InputWrapper>
                <CustomInput type="password" label="Password" viewModel={viewModel} fieldName="password" />
            </InputWrapper>
            <Link component={RouterLink} to="forgotpassword">
                <ForgotPasswordLink>Forgot password</ForgotPasswordLink>
            </Link>
        </Box>
    </Row>
);

interface ILoginSubmitButtonProps {
    isLoading: boolean;
}

const LoginSubmitButton: React.FC<ILoginSubmitButtonProps> = ({ isLoading }) => (
    <Row>
        <StyledButton style={{ margin: 0 }} type="submit">
            {isLoading ? <span>Please wait...</span> : <span>Login</span>}
        </StyledButton>
    </Row>
);

const LoginImageBackground: React.FC = () => <BackgroundImageContainer></BackgroundImageContainer>;
// endregion
